import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero, useTitle } from "../components/PageHero";
import { Reveal, Tag } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { PRODUCTS, PRODUCT_CATEGORIES } from "../data/products";

export default function Products() {
  useTitle("Industrial Products — Wear Parts, Fabrication, Tanks & Silos | SABAKT KSA");
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <main>
      <PageHero image="/images/cement/c19.jpg" eyebrow="Industrial Products" testid="products-hero"
        title="Products Built in Our Workshop. Proven in Your Plant." accent="Workshop."
        text="Wear parts, fabrication products, tanks and silos — manufactured in Riyadh, photographed in our workshop, and delivered across the Kingdom." />

      {PRODUCT_CATEGORIES.map((cat, ci) => {
        const items = PRODUCTS.filter((p) => p.category === cat.slug);
        return (
          <section key={cat.slug} id={cat.slug} className={`scroll-mt-24 border-b border-seam py-20 lg:py-24 ${ci % 2 ? "bg-iron" : ""}`} data-testid={`product-category-${cat.slug}`}>
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
              <Reveal>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-spark">{String(ci + 1).padStart(2, "0")}</span>
                  <Tag>Product Category</Tag>
                  <span className="h-px flex-1 bg-seam" />
                </div>
                <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-frost sm:text-5xl">
                  {cat.name}
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-relaxed text-cadmium">{cat.blurb}</p>
              </Reveal>
              <div className="mt-12 grid gap-px border border-seam bg-seam sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {items.map((p, i) => (
                  <Reveal key={p.slug} delay={(i % 4) * 0.06} className="h-full">
                    <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`}
                      className="group flex h-full flex-col bg-obsidian transition-colors duration-300 hover:bg-steel">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={p.image} alt={p.name} loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                      <div className="flex flex-1 items-center justify-between gap-3 p-5">
                        <div>
                          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-frost group-hover:text-spark">{p.name}</h3>
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-cadmium">{p.overview}</p>
                        </div>
                        <ArrowUpRight size={18} className="shrink-0 text-cadmium transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-spark" />
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <section className="border-b border-seam bg-iron py-20 lg:py-28" data-testid="diffusion-partnership">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
          <Reveal>
            <Tag>Technical Partnership</Tag>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-frost sm:text-5xl">
              Authorized Channel Partner of Diffusion Engineers Limited
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cadmium">
              SABAKT is the authorized channel partner of Diffusion Engineers Limited — one of India's most prestigious engineering companies specializing in wear solutions and maintenance technology.
            </p>
            <p className="mt-4 text-base leading-relaxed text-cadmium">
              With strong technical support from Diffusion Engineers, we deliver reconditioning and hardfacing solutions, refurbishment of worn-out components, on-site repair and maintenance services, and wear protection using advanced welding consumables — cost-effective life enhancement for heavy industry across Saudi Arabia.
            </p>
            <div className="mt-8">
              <Link to="/rfq" data-testid="diffusion-cta"
                className="inline-block bg-gold px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition-colors hover:bg-spark">
                Explore Wear & Welding Solutions →
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden border border-seam">
              <img src="/images/cement/c12.jpg" alt="Diffusion Engineers wear solutions at SABAKT workshop" className="w-full object-cover" />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-cadmium/60">
              Diffusion Engineers wear solutions — SABAKT workshop, Riyadh
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand title="Need a Product Specification?" text="Tell us the application — material handled, wear pattern, operating temperature — and we'll specify the product, not just quote it." />
    </main>
  );
}
